import image from '@/assets/user_pic.png';
import { IUser } from '@/redux/models';
import { useAppSelector } from '@/redux/store';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Avatar, Chip, Container, Divider, Fade, Grid, LinearProgress, Paper, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { SyntheticEvent, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AjustesCuenta from './AjustesCuenta';
import FiltrarMisInformes from './Informes/FiltrarMisInformes';
import Seguridad from './Seguridad';
import MisCarpetas from './carpetas/MisCarpetas';


interface TabPanelProps {
    value: string;
    isMobile?: boolean
}

function TabPanelHandler({ value, isMobile = false }: TabPanelProps) {
    const ChoisePage = (value: string) => {
        switch (value) {
            case 'cuenta':
                return <AjustesCuenta />;
            case 'seguridad':
                return <Seguridad />;
            case 'informes':
                return <MisCarpetas />;
            case 'filtrarInformes':
                return <FiltrarMisInformes />;
            default:
                return <AjustesCuenta />;
        }
    }

    return (
        <Fade in={true} timeout={300}>
            <div
                role="tabpanel"
                id={`vertical-tabpanel-${value}`}
                aria-labelledby={`vertical-tab-${value}`}
            >
                <Box p={isMobile ? 2 : 3}>
                    {ChoisePage(value)}
                </Box>
            </div>
        </Fade>
    );
}


const RouteList = [
    {
        label: 'Carpetas',
        value: 'informes',
        icon: <FolderIcon fontSize="small" />,
        description: 'Gestión de carpetas y documentos'
    },
    {
        label: 'Cuenta',
        value: 'cuenta',
        icon: <PersonIcon fontSize="small" />,
        description: 'Información personal y configuración de cuenta'
    },
    {
        label: 'Seguridad',
        value: 'seguridad',
        icon: <SecurityIcon fontSize="small" />,
        description: 'Contraseña y configuración de seguridad'
    }
];

const ProfileLayout = () => {
    let { profilePage } = useParams();
    const user = useAppSelector((state) => state.userState.user) as IUser;
    const navigate = useNavigate();
    const handleTabChange = (event: SyntheticEvent, newValue: string) => {
        navigate(`/perfil/${newValue}`);
    };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Calculate profile completion
    const calculateProfileCompletion = () => {
        const fields = [user.nombre, user.apellido, user.email, user.empresa, user.telefono, user.fotoPerfil];
        const completedFields = fields.filter(field => field && field.trim() !== '').length;
        return Math.round((completedFields / fields.length) * 100);
    };

    const profileCompletion = calculateProfileCompletion();

    useEffect(() => {
        if (!profilePage) {
            navigate(`/perfil/${RouteList[0].value}`);
        }
        if (!RouteList.find((route) => route.value === profilePage)) {
            navigate(`/perfil/${RouteList[0].value}`);
        }
    }, [profilePage, navigate]);

    if (!profilePage) return <></>;

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header Section */}
            <Paper
                elevation={2}
                sx={{
                    p: 3,
                    mb: 3,
                    backgroundColor: theme.palette.background.paper,
                    borderTop: `4px solid ${theme.palette.primary.main}`,
                    borderRadius: 2
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    <Grid item>
                        <Avatar
                            alt={user.nombre}
                            src={user.fotoPerfil || image}
                            sx={{
                                width: 80,
                                height: 80,
                                border: `3px solid ${theme.palette.primary.main}`,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                            }}
                        />
                    </Grid>
                    <Grid item xs>
                        <Typography variant="h4" fontWeight="bold" gutterBottom color="primary">
                            ¡Hola, {user.nombre}!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            Gestiona tu cuenta y configuración desde acá.
                        </Typography>

                        {/* Profile Completion */}
                        <Box sx={{ mb: 2 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Typography variant="body2">
                                    Completitud del perfil: {profileCompletion}%
                                </Typography>
                                {profileCompletion === 100 && (
                                    <VerifiedIcon fontSize="small" color="success" />
                                )}
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={profileCompletion}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: theme.palette.grey[200],
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: profileCompletion === 100 ? theme.palette.success.main : theme.palette.warning.main,
                                        borderRadius: 3,
                                    },
                                }}
                            />
                        </Box>

                        {/* Quick Stats */}
                        <Box display="flex" gap={2} flexWrap="wrap">
                            <Chip
                                icon={<BusinessIcon fontSize="small" />}
                                label={user.empresa || 'Sin empresa'}
                                size="small"
                                variant="outlined"
                                color="primary"
                            />
                            <Chip
                                icon={<FolderIcon fontSize="small" />}
                                label="Gestión de carpetas"
                                size="small"
                                variant="outlined"
                                color="primary"
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Main Content */}
            <Grid container spacing={3}>
                {/* Sidebar Navigation */}
                <Grid item md={3} xs={12}>
                    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box p={2}>
                            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                                Configuración
                            </Typography>
                        </Box>
                        <Divider />
                        <Tabs
                            orientation={isMobile ? "horizontal" : "vertical"}
                            variant="scrollable"
                            value={profilePage}
                            onChange={handleTabChange}
                            aria-label="Opciones de perfil"
                            sx={{
                                '& .MuiTab-root': {
                                    alignItems: 'flex-start',
                                    textAlign: 'left',
                                    minHeight: 60,
                                    justifyContent: 'flex-start',
                                    px: 3,
                                    py: 2,
                                },
                                '& .Mui-selected': {
                                    backgroundColor: theme.palette.primary.main + '10',
                                    borderRight: isMobile ? 'none' : `3px solid ${theme.palette.primary.main}`,
                                    borderBottom: isMobile ? `3px solid ${theme.palette.primary.main}` : 'none',
                                },
                            }}
                        >
                            {RouteList.map((route) => (
                                <Tab
                                    key={route.value}
                                    value={route.value}
                                    label={
                                        <Box display="flex" alignItems="center" gap={1.5} width="100%">
                                            {route.icon}
                                            <Box textAlign="left">
                                                <Typography variant="body2" fontWeight="medium">
                                                    {route.label}
                                                </Typography>
                                                {!isMobile && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {route.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    }
                                />
                            ))}
                        </Tabs>
                    </Paper>
                </Grid>

                {/* Main Content Area */}
                <Grid item md={9} xs={12}>
                    <Paper elevation={1} sx={{ borderRadius: 2, minHeight: 500 }}>
                        <TabPanelHandler value={profilePage as string} isMobile={isMobile} />
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default ProfileLayout;